export type AccessUser = {
  id: string;
  email: string;
  status: string;
  role: string | null;
  roles: { id: string; name: string; scopeType?: string; scopeId?: string | null; expiresAt?: string | null }[];
};

export type AccessRole = {
  id: string;
  name: string;
  description?: string | null;
  permissions: string[];
  isSystem?: boolean;
};

export type AccessMember = {
  id: string;
  status: string;
  user: { id: string; email: string };
  roles: { id: string; name: string; scopeType?: string; scopeId?: string | null; expiresAt?: string | null }[];
  profile?: {
    functionGroups: string[];
    department?: string | null;
    jobTitle?: string | null;
  } | null;
};

export type AccessInvite = {
  id: string;
  email: string;
  status: string;
  expiresAt: string;
  createdAt: string;
};

export type AccessUsersResponse = {
  ok: boolean;
  rbacEnabled: boolean;
  users: AccessUser[];
};

export type AccessMembersResponse = {
  ok: boolean;
  rbacEnabled: boolean;
  members: AccessMember[];
  invites: AccessInvite[];
};

export type AccessRolesResponse = {
  ok: boolean;
  roles: AccessRole[];
};

type AccessError = Error & {
  status: number;
};

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const message = payload?.error ?? "Request failed";
    const error = Object.assign(new Error(message), { status: response.status }) as AccessError;
    throw error;
  }
  return (await response.json()) as T;
}

export async function fetchAccessUsers(): Promise<AccessUsersResponse> {
  const response = await fetch("/api/admin/access/users", { headers: { accept: "application/json" } });
  return handleResponse<AccessUsersResponse>(response);
}

export async function fetchAccessRoles(): Promise<AccessRolesResponse> {
  const response = await fetch("/api/admin/access/roles", { headers: { accept: "application/json" } });
  return handleResponse<AccessRolesResponse>(response);
}

export async function fetchAccessMembers(): Promise<AccessMembersResponse> {
  const response = await fetch("/api/admin/access/members", { headers: { accept: "application/json" } });
  return handleResponse<AccessMembersResponse>(response);
}

export async function assignAccessRole(userId: string, roleId: string, reason: string): Promise<void> {
  const response = await fetch("/api/admin/access/assign", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ userId, roleId, reason }),
  });
  await handleResponse(response);
}

export async function revokeAccessRole(userId: string, roleId: string, reason: string): Promise<void> {
  const response = await fetch("/api/admin/access/revoke", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ userId, roleId, reason }),
  });
  await handleResponse(response);
}

export async function toggleRbacEnabled(enabled: boolean, reason: string): Promise<{ rbacEnabled: boolean }> {
  const response = await fetch("/api/admin/access/toggle", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ enabled, reason }),
  });
  return handleResponse<{ rbacEnabled: boolean }>(response);
}

export async function inviteMember(
  email: string,
  roleAssignments: Array<{ roleId: string }> = [],
  reason: string,
): Promise<{ invite: AccessInvite; token: string }> {
  const response = await fetch("/api/admin/access/invites", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ email, roleAssignments, reason }),
  });
  return handleResponse(response);
}

export async function updateMemberProfile(
  memberId: string,
  payload: { functionGroups: string[]; department?: string; jobTitle?: string },
): Promise<void> {
  const response = await fetch(`/api/admin/access/members/${memberId}/profile`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(payload),
  });
  await handleResponse(response);
}

export async function updateMemberStatus(memberId: string, status: string, reason: string): Promise<void> {
  const response = await fetch(`/api/admin/access/members/${memberId}/status`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ status, reason }),
  });
  await handleResponse(response);
}

export async function fetchEntitlements(tenantId?: string): Promise<{ entitlements: { key: string; enabled: boolean }[] }> {
  const url = tenantId ? `/api/admin/entitlements?tenantId=${encodeURIComponent(tenantId)}` : "/api/admin/entitlements";
  const response = await fetch(url, { headers: { accept: "application/json" } });
  return handleResponse(response);
}

export async function updateEntitlement(
  key: string,
  enabled: boolean,
  reason: string,
  tenantId?: string,
): Promise<void> {
  const response = await fetch("/api/admin/entitlements", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ key, enabled, reason, tenantId }),
  });
  await handleResponse(response);
}

export async function fetchAuditEvents(tenantId?: string): Promise<{ events: any[] }> {
  const url = tenantId ? `/api/admin/audit?tenantId=${encodeURIComponent(tenantId)}` : "/api/admin/audit";
  const response = await fetch(url, { headers: { accept: "application/json" } });
  return handleResponse(response);
}

export async function startImpersonation(
  tenantId: string,
  userId: string | null,
  reason: string,
): Promise<void> {
  const response = await fetch("/api/admin/impersonation/start", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ tenantId, userId, reason }),
  });
  await handleResponse(response);
}

export async function stopImpersonation(reason: string): Promise<void> {
  const response = await fetch("/api/admin/impersonation/stop", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ reason }),
  });
  await handleResponse(response);
}

export async function createRole(
  name: string,
  description: string | null,
  permissions: string[],
  reason: string,
): Promise<void> {
  const response = await fetch("/api/admin/access/roles", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ name, description, permissions, reason }),
  });
  await handleResponse(response);
}

export async function duplicateRole(roleId: string, reason: string): Promise<void> {
  const response = await fetch(`/api/admin/access/roles/${roleId}/duplicate`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ reason }),
  });
  await handleResponse(response);
}

export async function updateRole(
  roleId: string,
  payload: { name?: string; description?: string; permissions?: string[] },
  reason: string,
): Promise<void> {
  const response = await fetch(`/api/admin/access/roles/${roleId}`, {
    method: "PATCH",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ ...payload, reason }),
  });
  await handleResponse(response);
}

export async function deleteRole(roleId: string, reason: string): Promise<void> {
  const response = await fetch(`/api/admin/access/roles/${roleId}`, {
    method: "DELETE",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ reason }),
  });
  await handleResponse(response);
}
